import { useState, type FormEvent } from "react";
import { Play, RefreshCcw, Workflow } from "lucide-react";
import { Badge, Button, Card, Field } from "../components/ui";
import { DataTable } from "../components/DataTable";
import { useWorkflowQueue } from "../data/supergestor-data";
import { createFakeWorkflowInstance } from "../data/fake-workflow-store";
import { env } from "../lib/env";
import { useNavigate } from "react-router-dom";

export function WorkflowsPage() {
  const query = useWorkflowQueue();
  const navigate = useNavigate();
  const [newInstanceOpen, setNewInstanceOpen] = useState(false);
  const [workflowName, setWorkflowName] = useState("Revisão interna de contrato");
  const [notice, setNotice] = useState("");

  function createDemoInstance(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!env.useMocks) return;
    const entityType = workflowName === "Telemedicina (simulação)" ? "DEMO_CASE" : "CONTRACT";
    createFakeWorkflowInstance({ workflow: workflowName, entityType });
    setNewInstanceOpen(false);
    setNotice("Instância sintética criada nesta sessão de demonstração.");
  }

  return (
    <div className="page-stack">
      <section className="metric-grid metric-grid-four">
        <article className="metric-card">
          <Workflow size={20} />
          <span>Instâncias</span>
          <strong>{query.data?.length ?? 0}</strong>
          <small>Fluxos ativos</small>
        </article>
        <article className="metric-card">
          <Play size={20} />
          <span>Ações pendentes</span>
          <strong>{query.data?.reduce((s, i) => s + i.pending, 0) ?? 0}</strong>
          <small>Worker</small>
        </article>
        <article className="metric-card">
          <RefreshCcw size={20} />
          <span>Falhas</span>
          <strong>{query.data?.reduce((s, i) => s + i.failed, 0) ?? 0}</strong>
          <small>Revisão manual</small>
        </article>
        <article className="metric-card">
          <span>Definições</span>
          <strong>5</strong>
          <small>Planos, vendas e locações</small>
        </article>
      </section>
      <Card
        title="Central de workflows"
        description={
          env.useMocks
            ? "Transições declarativas, requisitos e ações idempotentes. Novas instâncias são apenas dados sintéticos desta sessão."
            : "Workflows persistentes são monitorados aqui. A revisão interna de contratos é conduzida em Contratos, com estado e histórico únicos."
        }
        action={
          env.useMocks ? (
            <Button
              onClick={() => {
                setNotice("");
                setNewInstanceOpen(true);
              }}
            >
              <Play size={16} /> Nova instância
            </Button>
          ) : (
            <Button variant="secondary" onClick={() => navigate("/contratos")}>
              <Play size={16} /> Revisar contratos
            </Button>
          )
        }
      >
        {notice && (
          <p role="status" className="workflow-demo-notice">
            {notice}
          </p>
        )}
        {!env.useMocks && (
          <p className="field-hint" role="note">
            A revisão de contratos não cria uma segunda instância nesta central. Use a tela
            Contratos para iniciar, enviar, aprovar ou rejeitar a revisão interna.
          </p>
        )}
        {newInstanceOpen && env.useMocks && (
          <section
            className="workflow-instance-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="workflow-instance-title"
          >
            <form onSubmit={createDemoInstance}>
              <h2 id="workflow-instance-title">Nova instância de demonstração</h2>
              <p>
                Este registro usa dados fictícios e fica salvo apenas nesta sessão do navegador.
              </p>
              <Field label="Definição do workflow">
                <select
                  value={workflowName}
                  onChange={(event) => setWorkflowName(event.target.value)}
                  autoFocus
                >
                  <option>Revisão interna de contrato</option>
                  <option>Cadastro manual de cliente</option>
                  <option>Telemedicina (simulação)</option>
                </select>
              </Field>
              <div className="workflow-instance-actions">
                <Button type="submit">
                  <Play size={16} /> Iniciar simulação
                </Button>
                <Button type="button" variant="secondary" onClick={() => setNewInstanceOpen(false)}>
                  Cancelar
                </Button>
              </div>
            </form>
          </section>
        )}
        <DataTable
          rows={query.data ?? []}
          getKey={(row) => row.id}
          columns={[
            { key: "workflow", header: "Workflow", render: (row) => row.workflow },
            { key: "entity", header: "Entidade", render: (row) => row.entityType },
            {
              key: "state",
              header: "Estado",
              render: (row) => <Badge tone="info">{row.state}</Badge>,
            },
            { key: "pending", header: "Pendentes", render: (row) => row.pending },
            {
              key: "failed",
              header: "Falhas",
              render: (row) => <Badge tone={row.failed ? "danger" : "success"}>{row.failed}</Badge>,
            },
            { key: "updated", header: "Atualização", render: (row) => row.updatedAt },
          ]}
        />
      </Card>
    </div>
  );
}
